import { useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "wouter";
import type {
  TenantCustomersFeedResponseV2,
  TenantCustomersListSort,
} from "@orbit/api-zod";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Merchant Customers list — Phase 13.
 *
 * Phase 11 baseline (search + cursor pagination) + Phase 13 additions:
 *   • Sort dropdown — Recently joined / Top spenders / Lapsing.
 *   • Show inactive toggle — re-includes REVOKED relationships in the
 *     roster so the merchant can audit who recently disconnected.
 *   • Lapsing badge — flagged on rows that have not visited in 30+
 *     days, regardless of sort. Top-spenders sort surfaces total
 *     spend prominently; lapsing sort surfaces the days-since-visit.
 *
 * Display rules:
 *   • Anonymized rows render with name "Deleted Customer" (server
 *     substitutes); we render literally and disable the deep-link
 *     decoration so the row still works as a history pointer.
 *   • Revoked rows render with a dim style + REVOKED chip when the
 *     "show inactive" toggle is on.
 */

type SortValue = TenantCustomersListSort;
const SORTS: Array<{ value: SortValue; label: string }> = [
  { value: "recent", label: "Recently joined" },
  { value: "top-spenders", label: "Top spenders" },
  { value: "lapsing", label: "Lapsing (no visit 30+ days)" },
];

export function MerchantCustomersPage() {
  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortValue>("recent");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [data, setData] = useState<TenantCustomersFeedResponseV2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);

  const queryString = useMemo(() => {
    const sp = new URLSearchParams();
    sp.set("limit", "25");
    const trimmed = search.trim();
    if (trimmed) sp.set("search", trimmed);
    if (sort !== "recent") sp.set("sort", sort);
    if (includeInactive) sp.set("includeInactive", "true");
    const cursor = cursorStack[cursorStack.length - 1];
    if (cursor) sp.set("cursor", cursor);
    return sp.toString();
  }, [search, sort, includeInactive, cursorStack]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      const r = await merchantFetch<TenantCustomersFeedResponseV2>(
        `/api/v1/tenant/customers?${queryString}`,
      );
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setErr(`${r.code}: ${r.message}`);
        setData(null);
        return;
      }
      setData(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [queryString]);

  function nextPage() {
    if (data?.nextCursor) setCursorStack((s) => [...s, data.nextCursor]);
  }

  function prevPage() {
    if (cursorStack.length > 1) setCursorStack((s) => s.slice(0, -1));
  }

  function changeSort(next: SortValue) {
    setSort(next);
    setCursorStack([null]);
  }

  function toggleInactive() {
    setIncludeInactive((v) => !v);
    setCursorStack([null]);
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-2xl px-6 pt-8">
        <MerchantNav current="customers" />

        <h1 className="mt-6 text-2xl font-bold text-navy">Customers</h1>
        <p className="mt-1 text-sm text-ink-dark">
          Your enrolled customers. Tap one to see balances or apply a manual
          adjustment.
        </p>

        <section className="mt-5 space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-ink-dark">
              Search (name prefix or phone last-4)
            </span>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCursorStack([null]);
              }}
              placeholder="e.g. Maria or 1234"
              className="mt-1 input"
            />
          </label>

          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark">
                Sort
              </span>
              <select
                value={sort}
                onChange={(e) => changeSort(e.target.value as SortValue)}
                className="mt-1 input"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm text-ink-dark">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={toggleInactive}
              />
              Show inactive (revoked)
            </label>
          </div>
        </section>

        {err && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        <section className="mt-5">
          {loading && !data && (
            <p className="text-sm text-ink-dark">Loading…</p>
          )}
          {data && data.items.length === 0 && (
            <p className="text-sm text-ink-dark">No customers match.</p>
          )}
          <ul className="space-y-2">
            {data?.items.map((row) => {
              const isAnonymized = row.name === "Deleted Customer";
              const isRevoked = row.status === "REVOKED";
              const dimmed = isAnonymized || isRevoked;
              const lapsing = isLapsing(row.lastVisitAt);
              return (
                <li key={row.tenantCustomerId}>
                  <Link
                    href={`/merchant/customers/${row.tenantCustomerId}/${row.customerId}`}
                    className={`block card transition-colors ${
                      dimmed
                        ? "opacity-60 hover:opacity-80"
                        : "hover:border-navy"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-navy">
                          {row.name}
                          {isAnonymized && (
                            <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                              Deleted
                            </span>
                          )}
                          {!isAnonymized && isRevoked && (
                            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                              Revoked
                            </span>
                          )}
                          {!dimmed && lapsing && (
                            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                              Lapsing
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-ink-dark">
                          ··· {row.phoneLast4} · {row.totalVisits} visit
                          {row.totalVisits === 1 ? "" : "s"}
                          {row.lastVisitAt
                            ? ` · last ${new Date(row.lastVisitAt).toLocaleDateString()}`
                            : " · never visited"}
                        </p>
                      </div>
                      <div className="text-right text-xs">
                        {sort === "top-spenders" && row.totalSpend ? (
                          <p className="font-mono text-base font-semibold text-navy">
                            ₱{formatSpend(row.totalSpend)}
                          </p>
                        ) : sort === "lapsing" ? (
                          <p className="font-mono text-navy">
                            {daysSince(row.lastVisitAt)}d ago
                          </p>
                        ) : (
                          <>
                            <p className="font-mono text-navy">
                              {row.pointsBalance.toFixed(2)} pts
                            </p>
                            <p className="font-mono text-ink-dark">
                              {row.stampsCount} stamps
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          {data && (data.nextCursor || cursorStack.length > 1) && (
            <div className="mt-4 flex justify-between">
              <button
                type="button"
                disabled={cursorStack.length <= 1}
                onClick={prevPage}
                className="btn-secondary disabled:opacity-50"
              >
                Newer
              </button>
              <button
                type="button"
                disabled={!data.nextCursor}
                onClick={nextPage}
                className="btn-secondary disabled:opacity-50"
              >
                Older
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function isLapsing(lastVisitAt: string | null): boolean {
  if (!lastVisitAt) return true;
  const days =
    (Date.now() - new Date(lastVisitAt).getTime()) / (1000 * 60 * 60 * 24);
  return days >= 30;
}

function daysSince(lastVisitAt: string | null): string | number {
  if (!lastVisitAt) return "∞";
  return Math.floor(
    (Date.now() - new Date(lastVisitAt).getTime()) / (1000 * 60 * 60 * 24),
  );
}

function formatSpend(s: string): string {
  const n = Number.parseFloat(s);
  if (Number.isNaN(n)) return s;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
